// O provider de tempo real: uma conexão por loja aberta, viva enquanto o
// painel estiver aberto. Cada evento vira invalidação no cache do TanStack
// Query — a tela, ao montar, lê o cache já atualizado. É o que impede o
// carregamento sob demanda de virar "evento perdido": o evento não depende
// de a tela estar montada.
//
// Quem precisa do evento em si (a caixa de conversas põe a mensagem na tela
// sem esperar refetch) assina com `useEventosDoServidor`.
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { obterToken } from '../auth/firebase';
import { useLoja } from '../estado/loja';
import { abrirConexao, urlDoTempoReal, type EventoDoServidor, type Situacao } from './conexao';

/** Que consultas cada evento do servidor invalida. Vazio = ninguém escuta ainda. */
export const INVALIDA_POR_EVENTO: Record<string, string[][]> = {
  init: [['conversas']],
  message: [['conversas']],
  session_new: [['conversas']],
  session_info: [['conversas']],
  session_deleted: [['conversas']],
  pause_changed: [['conversas']],
  global_pause_changed: [['conversas']],
  agent_pause_changed: [['agentes']],
  wa_account_status: [['canais']],
  template_status: [['canais'], ['alertas']],
  campanha_pausada: [['transmissao']],
  campanha_concluida: [['transmissao']],
  transmissao_optout: [['transmissao']],
  order_created: [['pedidos']],
  comissao_fatura: [['plano']],
  assinatura_aviso: [['plano']],
};

export function aplicarEvento(cliente: Pick<QueryClient, 'invalidateQueries'>, evento: EventoDoServidor): number {
  const chaves = INVALIDA_POR_EVENTO[evento.type] || [];
  for (const queryKey of chaves) void cliente.invalidateQueries({ queryKey });
  return chaves.length;
}

export type Ouvinte = (evento: EventoDoServidor) => void;
const Contexto = createContext<Situacao>('parado');
/** Assina os eventos crus do servidor; devolve a função que cancela. */
export const ContextoDosEventos = createContext<(ouvinte: Ouvinte) => () => void>(() => () => undefined);

export function TempoRealProvider({ children }: { children: ReactNode }) {
  const tenantId = useLoja((s) => s.tenantId);
  // Atrás do Firebase Hosting o WebSocket não passa pelo rewrite: o /api/me diz
  // onde abrir (o Cloud Run direto). Sem isso, a própria origem, como sempre.
  const urlDoServidor = useLoja((s) => s.tempoRealUrl);
  const consultas = useQueryClient();
  const [situacao, setSituacao] = useState<Situacao>('parado');
  const ouvintes = useRef(new Set<Ouvinte>());
  const assinar = useCallback((o: Ouvinte) => { ouvintes.current.add(o); return () => { ouvintes.current.delete(o); }; }, []);

  useEffect(() => {
    if (!tenantId) return;
    const conexao = abrirConexao({
      url: urlDoServidor || urlDoTempoReal(),
      tenantId,
      obterToken,
      aoEvento: (evento) => { aplicarEvento(consultas, evento); for (const o of ouvintes.current) o(evento); },
      aoMudar: setSituacao,
    });
    return () => conexao.fechar();
  }, [tenantId, urlDoServidor, consultas]);

  return <Contexto.Provider value={situacao}><ContextoDosEventos.Provider value={assinar}>{children}</ContextoDosEventos.Provider></Contexto.Provider>;
}

export const useSituacaoDoTempoReal = () => useContext(Contexto);

/** O ouvinte mais recente sempre é o chamado, sem reassinar a cada render. */
export function useEventosDoServidor(ouvinte: Ouvinte) {
  const assinar = useContext(ContextoDosEventos);
  const atual = useRef(ouvinte);
  atual.current = ouvinte;
  useEffect(() => assinar((e) => atual.current(e)), [assinar]);
}
